import React, { useState } from 'react';
import { router, Head, Deferred } from '@inertiajs/react';
import { AlertTriangle, KeyRound, Settings, Trash2 } from 'lucide-react';
import { Alert, AlertTitle, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import CredentialDetails from '@/frontend/components/Credentials/CredentialDetails';
import CredentialWorkflowAccess, { type CredentialPraxisAccess } from '@/frontend/features/Praxis/CredentialWorkflowAccess';
import CredentialFormModal from '@/frontend/components/Credentials/CredentialFormModal';
import PostConnectionModal from '@/frontend/components/PostConnectionModal';
import { BackLink } from '@/frontend/components/BackLink';
import { usePermissions } from '@/frontend/hooks/usePermissions';
import { useJustConnectedWorker } from '@/frontend/hooks/useJustConnectedWorker';
import DashboardLayout from '@/frontend/layouts/DashboardLayout';
import type { Credential, AssignWorker } from '@/frontend/pages/Dashboard/Integrations/types';

interface Props {
  page_title: string;
  credential: Credential;
  digital_workers: AssignWorker[];
  praxis_access?: CredentialPraxisAccess;
}

const CredentialShowPage = ({ page_title, credential, digital_workers = [], praxis_access }: Props) => {
  const { can_manage_resources } = usePermissions();
  const [editModalOpen, setEditModalOpen] = useState(false);
  const assignedWorkers = digital_workers.filter((w) => w.assigned);
  const isActive = assignedWorkers.length > 0 || praxis_access?.grants.some((grant) => grant.current);
  const justConnected = useJustConnectedWorker(digital_workers);

  return (
    <>
      <Head title={page_title} />
      <div className="px-4 py-6">
        <div className="min-h-full space-y-6">
          {/* Cabeçalho no padrão de detalhe do portal: breadcrumb fantasma +
              título 19/650 + chip de status via tokens (nunca verde cru). */}
          <div>
            <BackLink label="Credenciais" fallbackHref="/dashboard/credentials" className="mb-2" />
            <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
              <div className="flex min-w-0 items-center gap-3">
                {/* Chave (não o dither de agente): uma credencial é um segredo da
                    org, não um colaborador. Quadrado com o tom da marca. */}
                <span className="flex size-11 shrink-0 items-center justify-center rounded-[10px] bg-brand/10 text-brand dark:bg-brand/15">
                  <KeyRound className="size-5" strokeWidth={1.75} />
                </span>
                <div className="min-w-0">
                  <div className="flex flex-wrap items-center gap-2">
                    <h1 className="truncate text-[19px] font-semibold tracking-[-0.01em] text-foreground" title={credential.name}>
                      {credential.name}
                    </h1>
                    {isActive ? (
                      <span className="inline-flex items-center gap-1.5 rounded-full bg-success/12 px-[9px] py-0.5 text-[11px] font-semibold text-success dark:bg-success/15">
                        <span className="size-1.5 rounded-full bg-success" aria-hidden />
                        Ativa
                      </span>
                    ) : (
                      <span className="inline-flex items-center rounded-full bg-secondary px-[9px] py-0.5 text-[11px] font-semibold text-muted-foreground">
                        Sem uso
                      </span>
                    )}
                  </div>
                  {credential.description && (
                    <p className="mt-1 break-words text-[13px] text-muted-foreground">{credential.description}</p>
                  )}
                </div>
              </div>
              {can_manage_resources && (
                <Button className="shrink-0" variant="outline" size="sm" onClick={() => setEditModalOpen(true)}>
                  <Settings className="size-4" strokeWidth={1.75} /> Editar
                </Button>
              )}
            </div>
          </div>

          {!can_manage_resources && (
            <Alert variant="warning">
              <AlertTriangle className="h-4 w-4" />
              <AlertTitle>Você não tem permissão para gerenciar credenciais.</AlertTitle>
              <AlertDescription>Você pode visualizar os dados, mas não pode modificá-los.</AlertDescription>
            </Alert>
          )}

          <CredentialDetails credential={credential} workers={digital_workers} canManage={can_manage_resources} />
          <Deferred data="praxis_access" fallback={<div className="rounded-xl border border-border bg-card p-6" aria-busy="true" />}>
            {praxis_access ? <CredentialWorkflowAccess key={`${credential.id}:${praxis_access.organization_id}`} credentialId={credential.id} access={praxis_access} canManage={can_manage_resources} /> : <></>}
          </Deferred>

          {can_manage_resources && (
            <div className="flex justify-end">
              <Button
                variant="ghost" size="sm"
                className="text-muted-foreground hover:text-destructive"
                onClick={() => window.confirm('Tem certeza que deseja excluir esta credencial?') && router.delete(`/dashboard/credentials/${credential.id}`)}
              >
                <Trash2 className="size-4" strokeWidth={1.75} /> Excluir credencial
              </Button>
            </div>
          )}
        </div>
      </div>

      <CredentialFormModal
        open={editModalOpen}
        onClose={() => setEditModalOpen(false)}
        postUrl={`/dashboard/credentials/${credential.id}`}
        title="Editar credencial"
        credential={credential}
      />

      {justConnected.worker && (
        <PostConnectionModal
          open={justConnected.open}
          onClose={justConnected.close}
          worker={justConnected.worker}
          context={credential.name}
        />
      )}

    </>
  );
};

CredentialShowPage.layout = (page: React.ReactElement) => <DashboardLayout>{page}</DashboardLayout>;

export default CredentialShowPage;
